<script lang="ts">
    import Fa from 'svelte-fa'
    import { faEdit, faTrashAlt, faCheck } from '@fortawesome/free-solid-svg-icons'
    import { createEventDispatcher, onMount } from 'svelte'
    import { AppointmentConsumption } from '../../modules/schedulerApi/appointments/appointment-consumption'
    import type { AppointmentItem } from '../../modules/schedulerApi/appointments/appointment-item'
    import { User } from '../../modules/usersApi/users/user'
    import ZentitySelect from '../_common/z/zentity-select.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'
    import Zinput from '../_common/z/zinput.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import ZContainerCol from '../_common/z/z-container-col.svelte'
    import ZinputBreak from '../_common/z/zinput-break.svelte'
    const dispatch = createEventDispatcher()

    let className: string = ''
    export { className as class }
    export let appointmentConsumption: AppointmentConsumption = undefined
    export let items: AppointmentItem[] = []

    let step = 1
    let isNew = false
    let user: User = undefined

    // sem default: forçar o atendente a escolher explicitamente evita marcar "usando" sem querer
    // (openNew/closeAfter falsos é o mesmo estado de "não escolhido ainda" e de "usando item aberto")
    let frascoStatus: 'open' | 'using' | 'close' | 'open_and_close' = null
    const frascoStatusOptions = [
        { value: null, label: 'Selecione...' },
        { value: 'open', label: 'Abrindo novo item' },
        { value: 'using', label: 'Usando item aberto' },
        { value: 'close', label: 'Finalizando item aberto' },
        { value: 'open_and_close', label: 'Abrindo e consumindo tudo' },
    ]

    function loadFrascoStatus() {
        if (appointmentConsumption.openNew && appointmentConsumption.closeAfter) frascoStatus = 'open_and_close'
        else if (appointmentConsumption.openNew) frascoStatus = 'open'
        else if (appointmentConsumption.closeAfter) frascoStatus = 'close'
        else frascoStatus = null
    }

    function frascoStatusChanged() {
        appointmentConsumption.openNew = frascoStatus === 'open' || frascoStatus === 'open_and_close'
        appointmentConsumption.closeAfter = frascoStatus === 'close' || frascoStatus === 'open_and_close'
    }

    // "Eu estou" só aparece pra produto fracionado. se o produto é trocado por um não fracionado (ou limpo), o
    // select some mas o estado ficava: quantidade escondida/forçada pelo "abrindo e consumindo tudo" e
    // openNew/closeAfter ainda true. decide pelo produto do evento, não pelo bind.
    function productChanged(service: { consumptionMode?: string } | null) {
        if (service?.consumptionMode === 'fractional' || !frascoStatus) return
        if (frascoStatus === 'open_and_close') appointmentConsumption.quantity = 0
        frascoStatus = null
        appointmentConsumption.openNew = false
        appointmentConsumption.closeAfter = false
    }

    // "abrindo e consumindo tudo": não pergunta quantidade, dá baixa no volume nominal inteiro do frasco.
    // reativo (não só no on:change do select) pra continuar em dia se o produto mudar depois de escolhido.
    // guard em appointmentConsumption: confirmEntity/cancelEntity o zeram com frascoStatus ainda 'open_and_close',
    // e este bloco roda de novo no mesmo flush — sem isso quebra e congela o componente (e o pai) na tela.
    $: if (frascoStatus === 'open_and_close' && appointmentConsumption) {
        appointmentConsumption.quantity = appointmentConsumption.service?.nominalVolume || 0
    }

    // só pede o atendente explicitamente quando há mais de um distinto entre os serviços do agendamento —
    // sem ambiguidade, o backend preenche sozinho (ver AppointmentConsumptionService#register)
    $: distinctAttendants = [...new Map((items || []).filter((el) => el.user).map((el) => [el.user.userId, el.user])).values()]

    // dedupe por serviceId: um mesmo serviço pode aparecer em mais de um item do agendamento (ex.: consumo
    // de 2 sessões do mesmo serviço) — sem isso, o <select> keyed por value quebra com chaves duplicadas
    $: performedServiceOptions = [
        ...new Map((items || []).filter((el) => el.service).map((el) => [el.serviceId, { value: el.serviceId, label: el.service.name }])).values(),
    ]

    async function editEntity() {
        loadFrascoStatus()
        step = 2
    }

    async function newEntity() {
        appointmentConsumption = new AppointmentConsumption()
        isNew = true
        loadFrascoStatus()
        step = 2
    }

    async function cancelEntity() {
        appointmentConsumption = undefined
        step = 1
        dispatch('cancel')
    }

    async function confirmEntity() {
        if (!validateFields()) return
        if (user) appointmentConsumption.userId = user.userId
        dispatch('added', appointmentConsumption)
        if (isNew) appointmentConsumption = undefined
        step = 1
        isNew = false
    }

    function validateFields() {
        if (!appointmentConsumption.service) {
            evaluateError('Informe o produto consumido')
            return false
        }
        appointmentConsumption.serviceId = appointmentConsumption.service.serviceId
        const isFractional = appointmentConsumption.service.consumptionMode === 'fractional'
        if (!isFractional && !(appointmentConsumption.quantity > 0)) {
            evaluateError('Informe a quantidade consumida')
            return false
        }
        if (isFractional && !frascoStatus) {
            evaluateError('Selecione uma opção em "Eu estou"')
            return false
        }
        if (isFractional && !(appointmentConsumption.quantity > 0) && !appointmentConsumption.openNew && !appointmentConsumption.closeAfter) {
            evaluateError('Informe a quantidade, ou marque abrir/finalizar frasco')
            return false
        }
        if (distinctAttendants.length > 1 && !user && !appointmentConsumption.userId) {
            evaluateError('Mais de um atendente neste agendamento — informe quem paga este consumo')
            return false
        }
        return true
    }

    function evaluateError(error) {
        window.error(error)
    }

    onMount(() => {
        if (!appointmentConsumption) {
            newEntity()
        } else if (appointmentConsumption.startInEditMode) {
            // veio preenchido automaticamente pela receita (BOM) — abre direto em edição pra o atendente
            // confirmar "Eu estou", que fica em branco por padrão (ver loadFrascoStatus)
            loadFrascoStatus()
            step = 2
        }
    })
</script>

{#if appointmentConsumption !== undefined}
    {#if step === 1}
        <div class="row line item-static {className}">
            <div class="col-md-3">{appointmentConsumption.service?.name || '-'}</div>
            <div class="col-md-4">
                {#if appointmentConsumption.quantity > 0}Qtd: {appointmentConsumption.quantity}{/if}
                {#if appointmentConsumption.openNew}<span class="badge badge_secondary ms-1">abriu frasco</span>{/if}
                {#if appointmentConsumption.closeAfter}<span class="badge badge_secondary ms-1">finalizou frasco</span>{/if}
            </div>
            <div class="col-md-4 nowrap">
                {#if appointmentConsumption.syncStatus === 'failed'}
                    <span class="badge badge_secondary" title={appointmentConsumption.syncError}>falha na sincronização</span>
                {:else if appointmentConsumption.syncStatus === 'pending' && appointmentConsumption.applied === false}
                    <span class="badge badge_secondary">baixa ao comparecer</span>
                {:else if appointmentConsumption.syncStatus === 'pending'}
                    <span class="badge badge_secondary">sincronizando...</span>
                {/if}
            </div>
            <div class="col-md-1 text-end">
                <Zbutton label="Editar" on:click={editEntity}></Zbutton>
            </div>
        </div>
    {/if}
    {#if step === 2}
        <ZContainer class="my-3">
            <ZContainerCol size={3} sizeM={12}>
                <ZentitySelect
                    fonte="servico"
                    label="Produto consumido"
                    params={{ type: 'consumable' }}
                    bind:value={appointmentConsumption.service}
                    on:change={(ev) => productChanged(ev.detail)}
                    on:clear={() => productChanged(null)}
                />
            </ZContainerCol>
            {#if performedServiceOptions.length > 0}
                <Zinput
                    size={3}
                    type="select"
                    label="Referente ao serviço (opcional)"
                    options={performedServiceOptions}
                    bind:value={appointmentConsumption.performedServiceId}
                ></Zinput>
            {/if}

            {#if appointmentConsumption.service?.consumptionMode === 'fractional'}
                <Zinput
                    size={2}
                    sizeM={12}
                    type="select"
                    label="Eu estou"
                    options={frascoStatusOptions}
                    bind:value={frascoStatus}
                    on:change={frascoStatusChanged}
                ></Zinput>
            {/if}
            {#if frascoStatus !== 'open_and_close'}
                <ZContainerCol size={1}>
                    <Zinput type="number" label="Qtde ({appointmentConsumption.service?.volumeUnit || ''})" bind:value={appointmentConsumption.quantity}
                    ></Zinput>
                </ZContainerCol>
            {:else}
                <ZContainerCol size={2}>
                    <div class="quantity-note">
                        Baixa integral: {appointmentConsumption.quantity}
                        {appointmentConsumption.service?.volumeUnit || ''}
                    </div>
                </ZContainerCol>
            {/if}

            {#if distinctAttendants.length > 1}
                <ZContainerCol size={2} sizeM={12}>
                    <ZentitySelect fonte="usuario" label="Quem paga" somenteIds={distinctAttendants.map((el) => el.userId)} bind:value={user}></ZentitySelect>
                </ZContainerCol>
            {/if}
            <ZContainerCol flex={true} sizeM={12} size={1}>
                <div class="spacer"></div>
                <Zbutton class="me-2" color="red" icon={faTrashAlt} on:click={cancelEntity}></Zbutton>
                <Zbutton color="primary" icon={faCheck} on:click={confirmEntity}></Zbutton>
            </ZContainerCol>
        </ZContainer>
    {/if}
{/if}

<style>
    .quantity-note {
        font-size: 12px;
        opacity: 0.75;
        padding-top: 22px;
    }
    @media (max-width: 900px) {
        /* a margem existia para o botão que o seletor antigo desenhava ao lado do campo; o
           ZentitySelect não desenha botão solto, então a regra ficou sem alvo */
        .edit-form {
            padding: 16px 0px !important;
        }
        .edit-outer {
            position: absolute;
            top: 0px;
            left: 0px;
            height: 100vh;
            width: 100vw;
            background: #99999999;
            z-index: 0;
        }
    }
</style>
